import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import astrocartographyStyles from "../Astrocartography.styles";
import { AstroAccent, accentVars } from "../astroTheme";

interface AstroSectionProps {
  icon: ReactNode;
  title: string;
  caption?: string;
  /** Short status shown on the right of the summary, e.g. "8 / 12". */
  badge?: string;
  accent: AstroAccent;
  expanded: boolean;
  onChange: () => void;
  /** Guidance shown under the controls, muted. */
  hint?: string;
  children: ReactNode;
}

/**
 * One control section. A plain MUI Accordion — it owns the expand/collapse
 * transition and height, and its appearance comes from the scoped theme; the
 * accent is injected as CSS custom properties on the root.
 */
const AstroSection = (props: AstroSectionProps) => {
  const classes = astrocartographyStyles;
  const {
    icon,
    title,
    caption,
    badge,
    accent,
    expanded,
    onChange,
    hint,
    children,
  } = props;

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      sx={accentVars(accent)}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
        <Box sx={classes.sectionIcon}>{icon}</Box>
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {title}
          </Typography>
          {caption && (
            <Typography variant="caption" noWrap>
              {caption}
            </Typography>
          )}
        </Stack>
        {badge && <Chip label={badge} size="small" />}
      </AccordionSummary>
      <AccordionDetails>
        {children}
        {hint && (
          <Typography variant="caption" display="block" mt={1.5}>
            {hint}
          </Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AstroSection;
